import {
	ButtonGroup,
	For,
	Grid,
	GridItem,
	Stack,
	Tabs,
	Text,
} from '@chakra-ui/react';
import type { Route } from 'next';
import React, { Fragment } from 'react';
import { FaTrophy } from 'react-icons/fa6';
import { LuChefHat } from 'react-icons/lu';
import {
	MdBusiness,
	MdOutlineLocationOn,
	MdOutlineAccountBalanceWallet,
	MdPersonOutline,
	MdPendingActions,
	MdTrendingUp,
	MdLoyalty,
	MdSpeakerNotes,
} from 'react-icons/md';

import { AppRoutes } from '@/constants';
import { useAuthStore } from '@/modules/authentication';
import { NextLinkButton } from '@/modules/navigation/presentation';
import { IconLabel, InterestedButton } from '@/modules/shared/presentation';

import { useJobDetailContext } from './JobDetail';

export const JobDetailJobDetail = () => {
	const {
		entryUrl,
		handleAddInterestedJob,
		handleRemoveInterestedJob,
		job,
	} = useJobDetailContext();
	const { isAuthenticated } = useAuthStore();

	const details = [
		{
			icon:  MdPersonOutline,
			label: 'Job Description',

			render: () => (
				<Stack gap="sm">
					<Text textStyle="bodyBold">
						{job.detail.summary}
					</Text>
					<Text textStyle="body">
						{job.detail.description}
					</Text>
				</Stack>
			),
		},
		{
			icon:  MdPersonOutline,
			label: 'Applicant Eligibility',

			render: () => (
				<Text textStyle="body">
					{job.detail.requirements}
				</Text>
			),
		},
		{
			icon:  MdOutlineLocationOn,
			label: 'Job Location',

			render: () => (
				<Text>
					{job.detail.location}
				</Text>
			),
		},
		{
			icon:  MdBusiness,
			label: 'Work Hour',

			render: () => (
				<Text>
					{job.detail.workingHours}
				</Text>
			),
		},
		{
			icon:  MdPendingActions,
			label: 'Employment Status',

			render: () => (
				<Stack gap="sm">
					<Text>
						{job.detail.employmentType}
					</Text>
					<Text>
						{job.detail.probation}
					</Text>
				</Stack>
			),
		},
		{
			icon:  MdOutlineAccountBalanceWallet,
			label: 'Salary',

			render: () => (
				<Stack gap="sm">
					<Text>
						{job.detail.salaryProgression}
					</Text>
					<For each={job.detail.salaryExample}>
						{(item, index) => (
							<Text key={`${job.id}-${item}-${index}`}>
								{item}
							</Text>
						)}
					</For>
				</Stack>
			),
		},
		{
			icon:  MdTrendingUp,
			label: 'Salary Increase',

			render: () => (
				<Text>
					{job.detail.salaryIncrease}
				</Text>
			),
		},
		{
			icon:  FaTrophy,
			label: 'Bonus',

			render: () => (
				<Text>
					{job.detail.bonusPolicy}
				</Text>
			),
		},
		{
			icon:  LuChefHat,
			label: 'Holiday / Vacation',

			render: () => (
				<Text>
					{job.detail.timeOff}
				</Text>
			),
		},
		{
			icon:  MdLoyalty,
			label: 'Employment Benefits',

			render: () => job.detail.benefits,
		},
		{
			icon:  MdSpeakerNotes,
			label: 'Employee Testimonials',

			render: () => (
				<For each={job.detail.testimonials}>
					{(item, index) => (
						<Stack gap={2} key={`testimonial-${index}`}>
							<Text textStyle="bodyBold">
								{item.title}
							</Text>
							<Text textStyle="body">
								{item.quote}
							</Text>
						</Stack>
					)}
				</For>
			),
		},
	];

	// TODO create entry page
	const url = job.hasApplied ? '' : entryUrl;

	return (
		<Tabs.Root defaultValue="detail" gap={0} variant="outline">
			<Tabs.List bg="transparent">
				<Tabs.Trigger _selected={{ color: 'primary.100' }} value="detail">
					Job Detail
				</Tabs.Trigger>
				<ButtonGroup
					flex={1}
					gap={6}
					justifyContent="flex-end"
					justifySelf="end"
					mr={6}
					w="full"
				>
					<NextLinkButton
						colorPalette="primary"
						disabled={job.hasApplied}
						href={isAuthenticated ? url as Route : AppRoutes.auth.login}
					>
						{job.hasApplied ? 'Applied' : 'Entry'}
					</NextLinkButton>
					<InterestedButton
						isSelected={job.hasSaved}
						onSelect={handleAddInterestedJob!}
						onUnselect={handleRemoveInterestedJob!}
						selectedLabel="Remove from interested"
						unselectedLabel="Add to interested"
					/>
				</ButtonGroup>
			</Tabs.List>
			<Tabs.Content p={0} value="detail">
				<Grid gridTemplateColumns="300px 1fr">
					<For each={details}>
						{detail => (
							<Fragment key={detail.label}>
								<GridItem
									bg="gray.10"
									borderTop="1px solid"
									borderTopColor="gray.15"
									p={6}
								>
									<IconLabel
										icon={detail.icon}
										iconStyle="round"
										label={detail.label}
									/>
								</GridItem>
								<GridItem
									alignItems="center"
									bg="white"
									borderTop="1px solid"
									borderTopColor="gray.15"
									p={6}
									textStyle="body"
								>
									{detail.render()}
								</GridItem>
							</Fragment>
						)}
					</For>
				</Grid>
			</Tabs.Content>
		</Tabs.Root>
	);
};
